// src/utils/ssrSafe.js
/**
 * Utilities for making components SSR-safe and prerender-friendly
 */
import React, { useState, useEffect } from 'react';

/**
 * Safely access window object
 */
export const safeWindow = typeof window !== 'undefined' ? window : {};

/**
 * Safely access localStorage
 */
export const safeLocalStorage = {
  getItem: (key) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    }
    return null;
  },
  setItem: (key, value) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(key, value);
      } catch (e) {
        // Ignore storage errors
      }
    }
  },
  removeItem: (key) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.removeItem(key);
      } catch (e) {
        // Ignore storage errors
      }
    }
  }
};

/**
 * Safely access sessionStorage
 */
export const safeSessionStorage = {
  getItem: (key) => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    }
    return null;
  },
  setItem: (key, value) => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {
        // Ignore storage errors
      }
    }
  },
  removeItem: (key) => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (e) {
        // Ignore storage errors
      }
    }
  }
};

/**
 * Check if running in a browser environment
 */
export const isBrowser = typeof window !== 'undefined';

/**
 * Check if running in a prerender environment
 */
export const isPrerendering = () => {
  return typeof navigator !== 'undefined' && 
    (navigator.userAgent === 'ReactSnap' || 
     navigator.userAgent.includes('Prerender') ||
     (typeof window !== 'undefined' && window.__PRERENDER_INJECTED));
};

/**
 * Safely get current URL
 */
export const getCurrentURL = () => {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.href;
  }
  return '';
};

/**
 * Safely scroll to position
 */
export const safeScrollTo = (x, y, behavior = 'auto') => {
  if (typeof window !== 'undefined' && window.scrollTo) {
    try {
      window.scrollTo({ top: y, left: x, behavior });
    } catch (e) {
      // Fallback for older browsers
      window.scrollTo(x, y);
    }
  }
};

/**
 * Safely add event listener
 */
export const safeAddEventListener = (element, event, handler, options) => {
  if (element && element.addEventListener) {
    element.addEventListener(event, handler, options);
    return () => element.removeEventListener(event, handler, options);
  }
  return () => {};
};

/**
 * Higher-order component to wrap components that need browser APIs
 */
export const withSSRSafety = (WrappedComponent) => {
  return function SSRSafeComponent(props) {
    const [isClient, setIsClient] = useState(false);
    
    useEffect(() => {
      setIsClient(true);
    }, []);

    // During SSR or before hydration, render a minimal version
    if (!isClient) {
      return <div data-ssr-placeholder="true" style={{ minHeight: '100px' }} />;
    }

    return <WrappedComponent {...props} />;
  };
};

/**
 * Hook for client-side only operations
 */
export const useClientSide = (callback, deps = []) => {
  useEffect(() => {
    if (isBrowser) {
      return callback();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, Array.isArray(deps) ? deps : []);
};
